/**
 * 权限服务
 * 封装所有权限相关的 API 调用和业务逻辑
 */
import { apiClient } from "../../../config/netConfig";
import { message } from "antd";
import { Operation } from "../../operations/services/operationService";

export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
}

const request = async <T>(url: string, options?: any): Promise<ApiResponse<T>> => {
  try {
    const response = await apiClient.request({ url, ...options });
    const responseData = response.data;

    if (response.status === 200) {
      if ("success" in responseData && "data" in responseData && "message" in responseData) {
        return responseData;
      }
      return { success: true, data: responseData, message: "操作成功" };
    } else {
      const errorMessage = responseData.message || `请求失败: ${response.status}`;
      message.error(errorMessage);
      return { success: false, data: responseData, message: errorMessage };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "请求失败";
    message.error(errorMessage);
    throw error;
  }
};

export const PermissionService = {
  getAdminPermissions: async (adminId: string): Promise<ApiResponse<{ permissions: Operation[] }>> => {
    return request(`/admin/${adminId}/all-permissions`);
  },

  // 获取管理员权限数量
  getAdminPermissionCount: async (adminId: string): Promise<number> => {
    try {
      console.log(`🔍 开始获取管理员 ${adminId} 的权限数量`);

      // 使用正确的API路径
      const response = await request(`/admin/${adminId}/all-permissions`);
      console.log(`📡 管理员 ${adminId} 权限接口响应:`, response);

      if (response.success && (response.data as any)?.permissions) {
        const permissions = (response.data as any).permissions;
        console.log(`📋 管理员 ${adminId} 权限列表长度:`, permissions.length);

        // 计算实际的功能权限数量（排除模块节点）
        const count = permissions.filter((p: any) => p.type !== "MODULE").length;
        console.log(`📊 管理员 ${adminId} 权限数量: ${count} (总权限: ${permissions.length}, 功能权限: ${count})`);
        return count;
      } else {
        console.log(`⚠️ 管理员 ${adminId} 权限数据格式不正确:`, response);
        return 0;
      }
    } catch (error) {
      console.error(`❌ 获取管理员 ${adminId} 权限数量失败:`, error);
      return 0;
    }
  },

  // 批量获取多个管理员的权限数量
  getBatchAdminPermissionCounts: async (adminIds: string[]): Promise<Record<string, number>> => {
    try {
      // 并行获取所有管理员的权限数量
      const promises = adminIds.map(async (adminId) => {
        try {
          const count = await PermissionService.getAdminPermissionCount(adminId);
          return { adminId, count };
        } catch (error) {
          console.error(`获取管理员 ${adminId} 权限数量失败:`, error);
          return { adminId, count: 0 };
        }
      });

      const results = await Promise.all(promises);
      const counts: Record<string, number> = {};
      results.forEach(({ adminId, count }) => {
        counts[adminId] = count;
      });

      return counts;
    } catch (error) {
      console.error("批量获取管理员权限数量失败:", error);
      return {};
    }
  },
};
